// Material
import { List } from '@mui/material'
import { styled } from '@mui/material/styles'
import { WPhotoIcon } from '@weblite/icons'
// Common
import { GroupIcon, AddUserIcon, LinkIcon } from '$common/icons'
// logic
import { useHall } from '$logic/hall/react-hook'
import { useRoom, useRoomAttribute } from '$logic/room/react-hook'
import {
  openAddHallMembers,
  openJoinHallRequests,
  openHallInviteLinks,
  openHallSubscribers,
  openSharedMedia,
} from '$logic/router'
import { MemberPermissions } from '$logic/types/subscription'
import { toLocale } from '$func/text'
// Localization
import { t } from '$i18n'
import { HallAdminsActionItem } from '$common/hall-admin-action-item'
import { useJoinHallRequests } from '$logic/subscription/react-hook'
import { JOIN_HALL_REQUEST_ENABLED } from '$logic/subscription/join-requests'
import { isHall } from '$logic/room'
import { ChatProfileActionItem } from '$common/chat-profile-action-item'

interface Props {
  hallId: string
  isGroup: boolean
  permissions: MemberPermissions
}

const StyledList = styled(List)`
  padding-top: 0;
  padding-bottom: 0;
  background-color: white;
`

export function HallProfileActions({ hallId, isGroup, permissions }: Props) {
  const { entity: hall, loading: hallLoading } = useHall(hallId)

  const roomType = useRoomAttribute('type', hallId)

  const { totalCount: joinRequestsCount } = useJoinHallRequests({ hallId })

  const onSharedMedia = async () => {
    openSharedMedia(hallId)
    analite('CHAT_SHARED_MEDIA_CLICK', { type: room?.type })
  }

  return (
    <StyledList>
      {permissions?.inviteViaLink && (
        <ChatProfileActionItem
          Icon={<LinkIcon />}
          onClick={() => openHallInviteLinks(hallId)}
        >
          {t('profile.hall.actions.inviteViaLink')}
        </ChatProfileActionItem>
      )}

      {JOIN_HALL_REQUEST_ENABLED && isHall(roomType) && permissions.isAdmin && (
        <ChatProfileActionItem
          Icon={<AddUserIcon />}
          onClick={() => openJoinHallRequests(hallId)}
          secondaryText={
            joinRequestsCount ? toLocale(joinRequestsCount) : undefined
          }
        >
          {t('profile.hall.actions.joinRequests')}
        </ChatProfileActionItem>
      )}

      {permissions?.addMembers && (
        <ChatProfileActionItem
          Icon={<AddUserIcon />}
          onClick={() => openAddHallMembers(hallId, false)}
          iconBgColor="success500"
        >
          {roomType === 'GROUP'
            ? t('profile.hall.actions.addMember')
            : t('profile.hall.actions.addSubscriber')}
        </ChatProfileActionItem>
      )}
      {(permissions?.isAdmin || isGroup) && (
        <ChatProfileActionItem
          Icon={<GroupIcon />}
          onClick={() => openHallSubscribers(hallId)}
          secondaryText={toLocale(hall?.membersCount)}
          secondaryTextLoading={hallLoading}
          iconBgColor="secondary500"
        >
          {roomType === 'GROUP'
            ? t('profile.hall.actions.members')
            : t('profile.hall.actions.subscribers')}
        </ChatProfileActionItem>
      )}
      {permissions?.isAdmin && roomType === 'CHANNEL' && (
        <HallAdminsActionItem hallId={hallId} />
      )}
      <ChatProfileActionItem
        Icon={<WPhotoIcon sx={{ color: 'red' }} />}
        onClick={onSharedMedia}
        iconBgColor="yellow700"
      >
        {t('profile.common.sharedMedia')}
      </ChatProfileActionItem>
    </StyledList>
  )
}
